import { setAuthToken } from "@/vue-apollo/links/auth";
import { onLogin, onLogout } from "@/vue-apollo/controllers";
import gql from 'graphql-tag';
import Web3 from 'web3';
import Web3Modal from "web3modal";
import WalletConnectProvider from "@walletconnect/web3-provider";
const GET_NONCE_QUERY = gql`
query GetWalletNonce($id: String!) {
  nonce: walletNonce(id: $id)
}
`
const GET_CURRENT_USER_QUERY = gql`
query {
  user: me {
    id
    isPremium
    passBalance
    isDiscordLinked
    premiumExpiresAt
    wallets {
      id
      name
      openseaProfile
      displayName
      passBalance
    }
  }
}
`
const LOGIN_MUTATION = gql`
mutation Login($id: String!, $signature: String!) {
    login(id: $id, signature: $signature) {
        token
    }
}
`

const LOGIN_WITH_DISCORD_MUTATION = gql`
mutation LoginWithDiscord($code: String!) {
    login: loginWithDiscord(code: $code) {
        token
    }
}
`

const LINK_WALLET_MUTATION = gql`
mutation LinkWallet($id: String!, $signature: String!, $force: Boolean) {
    linkWallet(id: $id, signature: $signature, force: $force)
}`

const UNLINK_WALLET_MUTATION = gql`
mutation UnlinkWallet($id: String!) {
    unlinkWallet(id: $id)
}`

function getApiTokenStorageKey(user) {
  return `auth.token.${user.id}`;
}

function getMessageToSign(address, nonce) {
  let str = '';
  str += 'Click to prove wallet ownership and sign in to Compass.\n\n';
  str += 'No transaction will be executed by signing this message, it is completely free.\n\n';
  str += 'Your session will expire in 7 days.\n\n';
  str += 'Wallet address:\n';
  str += `${address.toLowerCase()}\n\n`;
  str += 'Nonce:\n';
  str += nonce;

  return str;
}


const providerOptions = {
  // Example with injected providers
  injected: {
    display: {
      // logo: "data:image/gif;base64,INSERT_BASE64_STRING",
      name: "MetaMask",
      description: "Connect with the provider in your Browser"
    },
    package: null
  },
  // Example with WalletConnect provider
  walletconnect: {
    display: {
      // logo: "data:image/gif;base64,INSERT_BASE64_STRING",
      name: "WalletConnect",
      description: "Scan QR Code with your mobile wallet"
    },
    package: WalletConnectProvider,
    options: {
      infuraId: "6b36a3b7147146a1b3ea32ca1e2d62c4" // required
    }
  }
};

const $web3Modal = new Web3Modal({
  network: "mainnet", // optional
  cacheProvider: true, // optional
  providerOptions // required
});


export default function({ apolloClient }) { 
  return {
    namespaced: true,
    state: () => ({
      user: null,
    }),
    actions: {
      async init({ dispatch }) {
        // detect if we have a token to use
        // set token if so and init authentication
        const tokenPath = localStorage.getItem('auth.uid');
        if (!tokenPath) {
          return;
        }

        const token = localStorage.getItem(tokenPath);
        if (!token) {
          return;
        }

        setAuthToken(token);

        return dispatch('getUser');
      },
      async getProvider({ commit, state }) {
        if (state.web3Provider) {
          return state.web3Provider;
        }

        const provider = await $web3Modal.connect();
        commit('setWeb3Provider', provider);

        return state.web3Provider;
      },
      async signInWithWallet({dispatch}, provider) {
        const web3 = new Web3(provider);
          
        const [address] = await web3.eth.getAccounts().catch(() => []);

        if (!address) {
          throw new Error("Invalid provider or no connected accounts");
        }

        if (await dispatch('validateTokenForAddress', address)) {
          return dispatch('getUser');
        }
      
        const {data: {nonce}} = await apolloClient.query({
          client: 'v2',
          fetchPolicy: 'network-only',
          query: GET_NONCE_QUERY,
          variables: {
            id: address,
          }
        });

        const dataToSign = web3.utils.fromUtf8(getMessageToSign(address, nonce));
        
        const signature = await web3.eth.personal.sign(dataToSign, address);
        
        const {data: { login }} = await apolloClient.mutate({
          mutation: LOGIN_MUTATION,
          variables: {
            id: address,
            signature,
          }
        })
        setAuthToken(login.token);
        
        return dispatch('getUser').then(async res => {
          if (res === null) {
            return res;
          }
          const tokenKey = getApiTokenStorageKey(res);
          localStorage.setItem(tokenKey, login.token);
          localStorage.setItem('auth.uid', tokenKey);
          return res;
        });
      },
      async signInWithDiscord({dispatch}, code) {
        
        const {data, errors} = await apolloClient.mutate({
          mutation: LOGIN_WITH_DISCORD_MUTATION,
          variables: {
            code,
          }
        })

        if (!data?.login && errors.length) {
          switch (errors[0].extensions.code) {
            case 'USER_NOT_FOUND':
              throw new Error('No Compass account is associated with this Discord. Please authenticate with a wallet and link your Discord.');
            default:
              throw new Error('Failed to authenticate with Discord');
          }
        }

        setAuthToken(data.login.token);
        
        return dispatch('getUser').then(async res => {
          const tokenKey = getApiTokenStorageKey(res);
          localStorage.setItem(tokenKey, data.login.token);
          localStorage.setItem('auth.uid', tokenKey);
          return res;
        });
      },
      async linkWallet({ dispatch }, {provider, force = false}) {
        const web3 = new Web3(provider);
          
        const [address] = await web3.eth.getAccounts().catch(() => []);

        if (!address) {
          throw new Error("Invalid provider or no connected accounts");
        }
      
        const {data: {nonce}} = await apolloClient.query({
          client: 'v2',
          fetchPolicy: 'network-only',
          query: GET_NONCE_QUERY,
          variables: {
            id: address,
          }
        });

        const dataToSign = web3.utils.fromUtf8(getMessageToSign(address, nonce));
        
        const signature = await web3.eth.personal.sign(dataToSign, address);
        
        const {data, errors} = await apolloClient.mutate({
          mutation: LINK_WALLET_MUTATION,
          variables: {
            id: address,
            signature,
            force,
          }
        })

        if (!data?.linkWallet && errors.length) {
          switch (errors[0].extensions.code) {
            case 'WALLET_ALREADY_LINKED':
              throw new Error('WALLET_ALREADY_LINKED');
            default:
              throw new Error('Failed to link wallet');
          }
        }
        
        return dispatch('getUser');
      },
      async unlinkWallet({dispatch}, wallet) {
        await apolloClient.mutate({
          mutation: UNLINK_WALLET_MUTATION,
          variables: {
            id: wallet.id,
          }
        });

        return dispatch('getUser');
      },
      async getUser({ commit }) {
        const {data: {user}} = await apolloClient.query({
          query: GET_CURRENT_USER_QUERY,
          fetchPolicy: 'network-only',
        });

        if (user === null) {
          return null;
        }

        await onLogin(apolloClient);

        commit('setUser', user)

        return user;
      },
      async validateTokenForAddress(context, address) {
        const token = localStorage.getItem(getApiTokenStorageKey(address));
        if (!token) {
          return false;
        }

        setAuthToken(token);
        const {data: {user}} = await apolloClient.query({
          query: GET_CURRENT_USER_QUERY,
          fetchPolicy: 'network-only',
        });

        return user !== null;
      },
      async logout({ commit }) {
        commit('setUser', null);
        setAuthToken(null);
        await onLogout(apolloClient);
        localStorage.removeItem(
          localStorage.getItem('auth.uid')
        );
        localStorage.removeItem('auth.uid');
      },
    },
    mutations: {
      setUser(state, user) {
        state.user = user;
      },
      setWeb3Provider(state, provider) {
        state.web3Provider = provider;
      }
    },
    getters: {
      isLoggedIn(state) {
        return state.user !== null;
      },
      isPremium(state) {
        return state.user?.isPremium || false;
      },
      wallet(state) {
        return state.user?.wallet || null;
      },
      wallets(state) {
        return state.user?.wallets || [];
      },
      isWalletLinked(state) {
        return address => !!state.user?.wallets?.filter(item => item.id === address?.toLowerCase()).length;
      },
      passBalance(state) {
        return state.user?.passBalance || 0;
      }
    }
  };
}